/* eslint-disable no-console */
/**
 * App.js
 * Created by calhosh on 8/28/2020.
 */
/* eslint react/no-multi-comp: 0*/
import React, {Fragment, Suspense, memo, useMemo, useState, useEffect, useContext} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Route, Outlet, RouterProvider, createBrowserRouter, createRoutesFromElements, createMemoryRouter, useLocation} from 'react-router-dom';
import './globals.less';
import './app.less';
import Main from './Main/Main';
import LoaderContainer from './LoaderContainer';
import env from '../util/env';
import Utility from '../util/utilityfunctions';
import LoadingIndicator from './Common/LoadingIndicator/LoadingIndicator';
import Errors from './Common/Errors/Errors';
import {useIsDrawerMode} from '../util/Ares/customHooks';
import ErrorPage from './ErrorPage';
import {labels} from '../util/utils';
import ReactiveModal from './Common/Modal/ReactiveModal';
import {useFrameTitle, useMenuClick, useNav, useFormDetails, useMFE, useIsModalOpen, useResourceUpdates} from '../util/customHooks';
import {isEqual} from 'lodash';
import {CookieConsentModal, CookieSettings, adobeAnalytics} from '@ey/voltron-analytics';
import envConfig from '@ey/voltron-config';
import ToastMessageContainer from './Common/ToastMessage/ToastMessageContainer';
import styled from 'styled-components';
import {pageViewArgs} from './Common/AdobeAnalytics/actionHelper';
import {pageName, siteSection} from './Common/AdobeAnalytics/constants';
import {HostContext} from './EntryPoint.js';
import FourFortyRASReminder from './Reminders/FourFortyRASReminder';
import TopBarNav from './Common/TopBarNav/TopBarNav';
import {messageHub, messageType} from '../util/uiconstants';
import {useMessageHubListener} from '@ey/voltron-core';
import {DeepLinkRedirect} from '@ey/canvascoreservices/DeepLink';
import {rules as deepLinkMappingRules, helpers as deepLinkHelpers} from './DeepLinkMapping.js';

const searchParams = new URLSearchParams(location.search);
const engagementId = searchParams.get('engagementId') || searchParams.get('engagementid');

const COOKIE_SETTINGS_PATH = `/cookiesettings?engagementId=${engagementId}`;
const adobeAnalyticsEnabled = envConfig.adobeAnalytics?.enabled;
const DryDock = React.lazy(() => import('./Main/DryDock'));
const SpursDryDock = React.lazy(() => import('./Main/SpursDrydock'));
const InvinciblesDryDock = React.lazy(() => import('./Main/InvinciblesDryDock/InvinciblesDryDock'));
const Forms = React.lazy(() => import('./Main/Forms/CanvasFormContainer'));
const IndependenceLoader = React.lazy(() => import('./Main/Independence/IndependenceContainer'));
const ProfileLoader = React.lazy(() => import('./Main/Profile/ProfileContainer'));
const Ares = React.lazy(() => import('./Main/Ares/AresContainer'));
const WizardsDrydock = React.lazy(() => import('./Main/WizardsDryDock'));
const KnightRidersDrydock = React.lazy(() => import('./Main/KnightRidersDryDock'));
const MavericksDryDock = React.lazy(() => import('./Main/MavericksDryDock'));
const TaskView = React.lazy(() => import('./Main/Ares/Tasks/TaskView'));
const GuidedWorkflowV2Container = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/GuidedWorkflowV2Container'));
const ExtendedV2Container = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/ExtendedV2Container'));
const MinimalisticAres = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/MinimalisticAres/MinimalisticAres'));
const FitRedirectionContainer = React.lazy(() => import('./Main/Ares/FIT/FitRedirectionContainer'));
const TrackChangesV2Container = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/TrackChangesV2Container'));
const CompareResponsesV2Container = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/CompareResponsesV2Container'));
const CompareResponsesV2DrillDownContainer = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/CompareResponsesV2DrillDownContainer'));
const AttributesContainer = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/Attributes/AttributesContainer'));
const RelatedDocumentsContainer = React.lazy(() => import('./Main/Ares/Tasks/TaskView/V2/RelatedDocuments/RelatedDocumentsContainer'));
const StandaloneContainer = React.lazy(() => import('./Main/Ares/Standalone/StandaloneContainer'));
const BodiesContainer = React.lazy(() => import('./Main/Ares/Standalone/Bodies/BodiesContainer'));
const ReactiveFrameContainer = React.lazy(() => import('./Main/ReactiveFrameContainer'));
const RCDryDock = React.lazy(() => import('./Main/RCDryDock/RCDryDock'));
const JaguarsDryDock = React.lazy(() => import('./Main/JaguarsDryDock'));

const Layout = memo(({hasError = false, hideReactiveFrame = false, hasMemoryRouting = false, path = null, hasNavigation = false}) => {
	if (env.allowDrydocks()) {
		console.log('Drydocks are allowed in this environment');
	}

	useMenuClick();

	useFormDetails();

	useFrameTitle();

	useNav();

	const [isMFE] = useMFE();
	const resourceKey = useResourceUpdates();

	const isDrawerMode = useIsDrawerMode();
	const isModalOpen = useIsModalOpen();
	const location = useLocation();
	const refreshKey = location.state?.refreshKey;

	const showReactiveFrame = useMemo(() => {
		if (hideReactiveFrame) {
			return false;
		}

		if (Utility.pageInIframe()) {
			return false;
		}

		return true;
	}, [hideReactiveFrame]);

	return (
		<Fragment>
			{hasNavigation && !isDrawerMode && <TopBarNav />}
			{!isDrawerMode && <FourFortyRASReminder />}
			{hasError && <Errors />}
			{!isModalOpen && (
				<StyledGuidedWorkflowBanners className="StyledGuidedWorkflowBanners">
					<ToastMessageContainer className="APPToastMessageContainer" />
				</StyledGuidedWorkflowBanners>
			)}
			<Main key={resourceKey} isMFE={isMFE}>
				<ReactiveModal />
				<Suspense fallback={<LoadingIndicator styleName="SuspenseLoadingIndicator" show fullscreen />}>
					{showReactiveFrame && <ReactiveFrameContainer />}
					<Outlet key={refreshKey} />
				</Suspense>
			</Main>
		</Fragment>
	);
}, isEqual);

Layout.displayName = 'Layout';

const App = memo(({hasError = false, hasMemoryRouting = false, path = null, hideReactiveFrame = false, hasNavigation = false}) => {
	const dispatch = useDispatch();
	const displayCookieConsent = useSelector((state) => state.adobeAnalytics?.promptForConsent);
	const userPreferences = useSelector((store) => {
		return store.userPreferences;
	});
	const [routes, setRoutes] = useState(null);

	const trackRoute = (component, pageName, siteSection, isHomePage = false, isErrorPage = false, locale = null) => {
		const TrackedComponent = adobeAnalytics.TrackPageView(component, pageViewArgs(pageName, siteSection, isHomePage, isErrorPage));
		if (locale) {
			return <TrackedComponent locale={locale} />;
		}
		return <TrackedComponent />;
	};

	const basename = useContext(HostContext)?.basename || '/';

	// Adobe Analytics
	useEffect(() => {
		if (adobeAnalyticsEnabled && userPreferences?.emailAddress) {
			dispatch(
				adobeAnalytics.initAdobeAnalytics(userPreferences.emailAddress, !userPreferences.IsGuestUser, envConfig.adobeAnalytics.defaultDataLayer)
			).finally(() => {
				// ensures tracking is initialized before tracking the first page view
				setRoutes(getRoutes(basename));
			});
		} else {
			setRoutes(getRoutes(basename));
		}
	}, [userPreferences]);

	useEffect(() => {
		if (hasMemoryRouting && path && routes) {
			routes.navigate(path);
		}
	}, [hasMemoryRouting, path, routes]);

	const getRoutes = (basename) => {
		let routes = createRoutesFromElements(
			<Route
				element={
					<Layout hasError={hasError} hideReactiveFrame={hideReactiveFrame} hasMemoryRouting={hasMemoryRouting} path={path} hasNavigation={hasNavigation} />
				}
				errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
			>
				<Route
					path="/redirect"
					element={
						<DeepLinkRedirect
							ready={!!userPreferences}
							rules={deepLinkMappingRules}
							helpers={deepLinkHelpers}
							loadingFallback={<LoadingIndicator fullscreen />}
						/>
					}
				/>
				{env.allowDrydocks() ? (
					<React.Fragment>
						<Route path="/drydock" element={<DryDock />} />
						<Route path="/spurs/drydock" element={<SpursDryDock />} />
						<Route path="/invdrydock" element={<InvinciblesDryDock />} />
						<Route path="/wizardsdrydock" element={<WizardsDrydock />} />
						<Route path="/knightridersdrydock" element={<KnightRidersDrydock />} />
						<Route path="/mavericksdrydock" element={<MavericksDryDock />} />
						<Route path="/rcdrydock" element={<RCDryDock />} />
						<Route path="/dryjags" element={<JaguarsDryDock />} />
					</React.Fragment>
				) : (
					<></>
				)}
				<Route
					path="/conclusion"
					element={trackRoute(Forms, pageName.conclusion, siteSection.forms)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				/>
				<Route
					path="/profile"
					element={trackRoute(Forms, pageName.profile, siteSection.profile)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				/>
				<Route
					path="/independence"
					element={trackRoute(IndependenceLoader, pageName.independence, siteSection.independence)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				/>
				<Route
					path="/initProfile"
					element={trackRoute(ProfileLoader, pageName.profile, siteSection.profile)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				/>
				<Route path="/canvasform" errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}>
					<Route index element={trackRoute(Forms, pageName.forms, siteSection.forms)} />
					<Route path=":view" element={trackRoute(Forms, pageName.view, siteSection.forms)} />
					<Route path="changes" element={trackRoute(Forms, pageName.changes, siteSection.forms)} />
					<Route path="details" element={trackRoute(Forms, pageName.details, siteSection.forms)} />
				</Route>
				<Route
					path="/fit"
					element={trackRoute(FitRedirectionContainer, pageName.fit, siteSection.fit)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				/>
				<Route path="/guidedworkflow" errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}>
					<Route index element={trackRoute(Ares, pageName.guidedWorkFlow, siteSection.guidedWorkFlow)} />

					<Route path=":documentId" element={trackRoute(TaskView, pageName.document, siteSection.guidedWorkFlow)} />
					<Route path=":documentId/header/:headerId" element={trackRoute(TaskView, pageName.header, siteSection.guidedWorkFlow)} />
					<Route path=":documentId/header/:headerId/section/:sectionId" element={trackRoute(TaskView, pageName.section, siteSection.guidedWorkFlow)} />
					<Route path=":documentId/summary" element={trackRoute(TaskView, pageName.summary, siteSection.guidedWorkFlow)} />
				</Route>
				<Route path="/guidedworkflowv2/minimal" errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}>
					<Route index element={trackRoute(MinimalisticAres, pageName.guidedWorkFlowV2, siteSection.guidedWorkFlowV2)} />
					<Route path=":documentId" element={trackRoute(MinimalisticAres, pageName.document, siteSection.guidedWorkFlowV2)} />
					<Route path=":documentId/header/:headerId" element={trackRoute(MinimalisticAres, pageName.header, siteSection.guidedWorkFlowV2)} />
					<Route
						path=":documentId/header/:headerId/section/:sectionId"
						element={trackRoute(MinimalisticAres, pageName.section, siteSection.guidedWorkFlowV2)}
					/>
				</Route>
				<Route path="/guidedworkflowv2" errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}>
					<Route index element={trackRoute(Ares, pageName.guidedWorkFlowV2, siteSection.guidedWorkFlowV2)} />
					<Route path=":documentId" element={trackRoute(ExtendedV2Container, pageName.document, siteSection.guidedWorkFlowV2)} />
					<Route path=":documentId/header/:headerId" element={trackRoute(ExtendedV2Container, pageName.header, siteSection.guidedWorkFlowV2)} />
					<Route
						path=":documentId/header/:headerId/section/:sectionId"
						element={trackRoute(ExtendedV2Container, pageName.section, siteSection.guidedWorkFlowV2)}
					/>
					<Route path=":documentId/summary" element={trackRoute(GuidedWorkflowV2Container, pageName.summary, siteSection.guidedWorkFlowV2)} />
					<Route path="changes/:documentId" element={trackRoute(TrackChangesV2Container, pageName.changes, siteSection.guidedWorkFlowV2)} />
					<Route path="compare/:documentId" element={trackRoute(CompareResponsesV2Container, pageName.compare, siteSection.compareDocument)} />
					<Route
						path="compare/:documentId/header/:headerId/section/:sectionId"
						element={trackRoute(CompareResponsesV2DrillDownContainer, pageName.section, siteSection.compareDocument)}
					/>
					<Route path="attributes/:documentId" element={trackRoute(AttributesContainer, pageName.attributes, siteSection.attributes)} />
					<Route path="relateddocuments/:documentId" element={trackRoute(RelatedDocumentsContainer, pageName.relatedDocuments, siteSection.relatedDocuments)} />
				</Route>
				<Route
					path="/standalone"
					element={trackRoute(StandaloneContainer, pageName.standAlone, siteSection.standAlone)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				>
					<Route
						path=":documentId/header/:headerId/section/:sectionId/body/:bodyId"
						element={trackRoute(BodiesContainer, pageName.body, siteSection.standAlone)}
					/>
				</Route>
				<Route
					path="/cookiesettings"
					element={trackRoute(CookieSettings, pageName.cookieSettings, siteSection.consent, false, false, labels.cookieSettings)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				/>
				<Route
					path="*"
					element={trackRoute(Forms, pageName.forms, siteSection.forms, true)}
					errorElement={trackRoute(ErrorPage, pageName.errorPage, siteSection.errorPage, false, true)}
				/>
			</Route>
		);

		if (hasMemoryRouting) {
			return createMemoryRouter(routes, {
				initialEntries: [path || '/']
			});
		} else {
			return createBrowserRouter(routes, {
				basename: basename
			});
		}
	};

	const onCookieConsentResult = (type) => {
		dispatch(adobeAnalytics.setConsentResult(type));
	};

	return (
		<>
			{userPreferences.isPreferencesLoaded && routes ? <RouterProvider router={routes} /> : <LoaderContainer />}
			{displayCookieConsent && `${location.pathname}${location.search}` !== COOKIE_SETTINGS_PATH && (
				<CookieConsentModal
					settingsURL={COOKIE_SETTINGS_PATH}
					onResult={onCookieConsentResult}
					appName={labels.versionLabel}
					labels={{
						title: labels.aaCookieConsentTitle,
						prompt: labels.aaCookieContentPrompt,
						explanationHTML: labels.aaCookieConsentExplanation,
						explanationHTMLWithDoNotTrack: labels.aaCookieConsentExplanationWithDoNotTrack,
						declineOptionalAction: labels.aaCookieConsentDeclineOptionalAction,
						acceptAllAction: labels.aaCookieConsentAcceptAllAction,
						customizeAction: labels.aaCookieConsentCustomizeAction,
						customizeURL: labels.aaCookieConsentCustomizeURL
					}}
				/>
			)}
		</>
	);
}, isEqual);

App.displayName = 'App';

const StyledGuidedWorkflowBanners = styled.section`
	position: absolute;
	left: var(--px-60);
	width: calc(100% - var(--px-65));
	display: flex;
	flex-direction: column;
	z-index: 9;
	.bannerLabel.InsufficientRightsBannerLabel {
		width: calc(100vw - var(--px-160)) !important;
	}
`;

export default App;
